import { LIVE_EVENT_NAME, type LiveEntity, LiveEventSchema, ORG_ID_HEADER } from '@clinic/contracts';
import type { QueryClient } from '@tanstack/svelte-query';
import { refreshSession } from '../api/client';
import { keys } from '../api/keys';
import { sessionStore } from '../auth/session-store';
import { record } from '../diag/diag.svelte';

/** Which cached lists a change can make stale. Clinical (audited) queries are never listed. */
const STALE: Record<LiveEntity, readonly ((orgId: string) => readonly unknown[])[]> = {
  patient: [keys.patients, keys.appointments, keys.smsQueue],
  appointment: [keys.appointments, keys.smsQueue],
  visit: [keys.visits, keys.appointments, keys.smsQueue, keys.finance],
  service: [keys.services, keys.appointments],
  resource: [keys.resources],
  member: [keys.members, keys.practitioners],
  sms: [keys.smsQueue],
  sms_template: [keys.smsTemplates, keys.smsQueue],
  inventory: [keys.inventory],
  finance: [keys.finance],
};

function invalidateAll(qc: QueryClient, orgId: string): void {
  void qc.invalidateQueries({
    queryKey: keys.org(orgId),
    predicate: (q) => q.queryKey[2] !== 'clinical',
  });
}

export type LiveStatus = 'connecting' | 'live' | 'offline';

/** The server writes a comment line every 25 s; this long without a byte, the connection is dead. */
export const STALL_MS = 60_000;
/** Coming back to the page after this long without a byte (a ping is due every 25 s): start over. */
export const RESUME_STALE_MS = 40_000;
/** The server answers the opening request at once; this long without headers, try again. */
export const CONNECT_MS = 15_000;
const WATCH_EVERY_MS = 5_000;

export interface LiveStreamOptions {
  orgId: string;
  queryClient: QueryClient;
  onStatus: (status: LiveStatus) => void;
}

/**
 * The live connection (ADR-019), read with fetch so Authorization and X-Org-Id pass the normal
 * guard. An event names only what changed; the matching lists are marked stale and TanStack Query
 * re-reads them through the normal endpoints. After a reconnect everything (except clinical reads)
 * is refetched once, because events missed while disconnected are not replayed.
 *
 * A phone that slept or changed network leaves a connection that is dead without ever saying so
 * (the read never fails), so the stream would show «live» and deliver nothing, and the page would
 * keep waiting on a dead connection. Hence the watchdog: no byte for STALL_MS, or none for
 * RESUME_STALE_MS when the page comes back, and the connection is dropped and opened again at once.
 * Returns the function that closes it.
 */
export function startLiveStream({ orgId, queryClient: qc, onStatus }: LiveStreamOptions): () => void {
  const left = new AbortController();
  let current: AbortController | null = null;
  let streaming = false;
  let lastByte = 0;
  let skipWait = false;
  let wake: (() => void) | null = null;
  let attempt = 0;
  let connectedBefore = false;

  const restart = (why: string) => {
    record('live', `restart: ${why}`);
    skipWait = true;
    current?.abort();
  };
  const watchdog = setInterval(() => {
    if (streaming && Date.now() - lastByte > STALL_MS) restart('no data for a minute');
  }, WATCH_EVERY_MS);
  const onVisible = () => {
    if (document.visibilityState === 'visible' && streaming && Date.now() - lastByte > RESUME_STALE_MS)
      restart('back after a pause');
  };
  const onOnline = () => wake?.();
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);

  const run = async (): Promise<void> => {
    while (!left.signal.aborted) {
      onStatus('connecting');
      const conn = new AbortController();
      current = conn;
      const stop = () => conn.abort();
      left.signal.addEventListener('abort', stop, { once: true });
      const connectTimer = setTimeout(() => restart('no answer to the opening request'), CONNECT_MS);
      try {
        const token = sessionStore.get()?.accessToken;
        const res = await fetch('/v1/clinic/events', {
          headers: {
            accept: 'text/event-stream',
            ...(token ? { authorization: `Bearer ${token}` } : {}),
            [ORG_ID_HEADER]: orgId,
          },
          credentials: 'same-origin',
          signal: conn.signal,
        });
        clearTimeout(connectTimer);
        if (res.status === 401) {
          if (!(await refreshSession())) return;
          continue;
        }
        if (!res.ok || !res.body) throw new Error(`live updates: HTTP ${res.status}`);
        attempt = 0;
        streaming = true;
        lastByte = Date.now();
        onStatus('live');
        record('live', connectedBefore ? 'open again' : 'open');
        if (connectedBefore) invalidateAll(qc, orgId);
        connectedBefore = true;
        await read(
          res.body,
          () => (lastByte = Date.now()),
          (entity) => {
            for (const key of STALE[entity]) void qc.invalidateQueries({ queryKey: key(orgId) });
            void qc.invalidateQueries({ queryKey: keys.audit(orgId) });
          },
        );
      } catch {
        // network drop, a dropped dead connection or a malformed event: reconnect below (unless the page left)
        if (left.signal.aborted) return;
      } finally {
        clearTimeout(connectTimer);
        streaming = false;
        left.signal.removeEventListener('abort', stop);
        current = null;
      }
      // The server ends streams on purpose (token lifetime, re-authorization); back off a little.
      onStatus('offline');
      record('live', 'closed');
      attempt += 1;
      if (skipWait) {
        skipWait = false;
        continue;
      }
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          left.signal.removeEventListener('abort', done);
          wake = null;
          resolve();
        };
        const timer = setTimeout(done, attempt === 1 ? 500 : delay);
        wake = done;
        left.signal.addEventListener('abort', done, { once: true });
      });
    }
  };
  void run();

  return () => {
    left.abort();
    clearInterval(watchdog);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
  };
}

async function read(
  body: ReadableStream<Uint8Array>,
  onChunk: () => void,
  onChange: (entity: LiveEntity) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    onChunk();
    buffer += decoder.decode(value, { stream: true });
    let cut = buffer.indexOf('\n\n');
    while (cut >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const lines = block.split('\n');
      const data = lines.find((l) => l.startsWith('data: '));
      if (lines.includes(`event: ${LIVE_EVENT_NAME}`) && data) {
        const parsed = LiveEventSchema.safeParse(JSON.parse(data.slice(6)));
        if (parsed.success) onChange(parsed.data.entity);
      }
      cut = buffer.indexOf('\n\n');
    }
  }
}
